import Container from "@saleor/components/Container";
import { makeStyles } from "@saleor/macaw-ui";
import clsx from "clsx";
import { FormikProps } from "formik";
import React, { useEffect, useState } from "react";

interface UserGuideTableProps {
  formik: FormikProps<any>;
  userGroupList?: any[];
  tableContainerRef: React.RefObject<HTMLDivElement>;
  userGuidanceData?: any;
}

const useClasses = makeStyles(
  () => ({
    body: {
      fontFamily: "Arial, sans-serif",
      margin: "20px",
      display: "flex",
      flexDirection: "column",
      maxHeight: "72vh",
      position: "relative"
    },
    tableContainer: {
      flex: 1,
      overflowY: "auto",
      boxSizing: "border-box",
      borderRadius: "2px",
      "&:after": {
        content: "''",
        position: "absolute",
        left: 0,
        right: 14,
        bottom: "0px",
        borderBottom: "2px solid #00a1e0",
        zIndex: 2
      }
    },
    roleTable: {
      width: "100%",
      borderCollapse: "collapse",
      margin: "0",
      border: "2px solid #00a1e0",
      borderTop: "none"
    },
    tableCell: {
      border: "1px solid #ccc",
      padding: "10px",
      textAlign: "left"
    },
    tableHeader: {
      backgroundColor: "#eaf8fc",
      color: "#333",
      position: "sticky",
      top: 0,
      zIndex: 2,
      boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.1)",
      "&:after": {
        content: "''",
        position: "absolute",
        left: 0,
        right: 0,
        top: "-1px",
        borderTop: "2px solid #00a1e0",
        zIndex: 2
      },
      "&:before": {
        content: "''",
        position: "absolute",
        left: 0,
        right: 0,
        bottom: "-1px",
        borderBottom: "1px solid #ccc",
        zIndex: 2
      }
    },
    userGroup: {
      color: "#00a1e0",
      fontWeight: "bold",
      textAlign: "center",
      verticalAlign: "middle"
    },
    tableRow: {
      backgroundColor: "#f9f9f9"
    },
    list: {
      listStyleType: "none",
      paddingLeft: "0",
      margin: "0"
    }
  }),
  { name: "UserGuideTable" }
);

const UserGuideTable: React.FC<UserGuideTableProps> = props => {
  const { formik, userGroupList, tableContainerRef, userGuidanceData } = props;
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const classes = useClasses();

  useEffect(() => {
    const userGroupSelected = userGroupList.find(
      group => group.value === formik.values.userGroup
    );
    setSelectedUserGroup(userGroupSelected ? userGroupSelected.code : null);
  }, [formik.values.userGroup, userGroupList]);

  const renderMenuFunctions = menuFunctions =>
    menuFunctions.map((value, index) => (
      <li key={`${value}-${index}`}>
        {menuFunctions.length > 1 ? "- " : ""}
        {value}
      </li>
    ));

  const renderGroupRow = (
    userGroupName,
    userRole,
    menuFunctions,
    groupIndex,
    groupsLength
  ) => (
    <tr key={`${userGroupName}-${userRole}`} className={classes.tableRow}>
      {groupIndex === 0 && (
        <td
          rowSpan={groupsLength}
          className={clsx(classes.tableCell, classes.userGroup)}
        >
          {userGroupName}
        </td>
      )}
      <td className={classes.tableCell}>{userRole}</td>
      <td className={classes.tableCell}>
        <ul className={classes.list}>{renderMenuFunctions(menuFunctions)}</ul>
      </td>
    </tr>
  );

  const renderUserGroupRows = (userGroupName, groups) =>
    groups.map((group, groupIndex) => {
      const userRole = group.name;
      const menuFunctions = group.menuFunctions.map(func => func.name);

      return renderGroupRow(
        userGroupName,
        userRole,
        menuFunctions,
        groupIndex,
        groups.length
      );
    });

  const renderRows = () => {
    const rows = [];
    if (userGuidanceData?.getRolesAndMenuFunctions) {
      userGuidanceData.getRolesAndMenuFunctions.edges.forEach(edge => {
        const userGroup = edge?.node?.code;
        const userGroupName = edge?.node?.name;
        const groups = edge?.node?.groups;

        if (selectedUserGroup === "ALL" || selectedUserGroup === userGroup) {
          rows.push(...renderUserGroupRows(userGroupName, groups));
        }
      });
    }
    return rows;
  };

  return (
    <Container>
      <div className={classes.body}>
        <div className={classes.tableContainer} ref={tableContainerRef}>
          <table className={classes.roleTable}>
            <thead>
              <tr>
                <th className={clsx(classes.tableCell, classes.tableHeader)}>
                  User Group
                </th>
                <th className={clsx(classes.tableCell, classes.tableHeader)}>
                  User Roles
                </th>
                <th className={clsx(classes.tableCell, classes.tableHeader)}>
                  Menu Functions
                </th>
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

export default UserGuideTable;
